
/**
 * Cookie Service 
 * Handles custom cookie operations based on consent status
 */

/**
 * Initialize the visitor cookie for chat functionality
 * Only creates a cookie if consent is granted
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.siteName - Site identifier for API calls
 * @param {string} config.backendBaseUrl - Base URL for backend API
 * @param {boolean} config.hasConsent - Whether consent is granted
 * @param {boolean} [config.debug=false] - Enable debug logging
 * @returns {Promise<Object>} Promise with the API response
 */
export const initializeVisitorCookie = async ({ siteName, backendBaseUrl, hasConsent, debug = false }) => {
  // If no consent, don't even attempt to create a cookie
  if (!hasConsent) {
    // debug && console.log('[Cookie] No consent granted, skipping cookie initialization');
    return { success: true, message: 'No cookie created (consent not granted)', ephemeral: true };
  }
  
  try {
    // Always log this for // debugging
    // const timestamp = new Date().toISOString();
    // debug && console.log(`[Cookie DEBUG][${timestamp}] Setting visitor cookie for ${siteName} (consent granted)`);
    const endpoint = `${backendBaseUrl}/chat-api/${siteName}/init?cookies_accepted=true`;
    // debug && console.log(`[Cookie DEBUG][${timestamp}] API call to: ${endpoint}`);
    
    // debug && console.log(`[Cookie DEBUG][${timestamp}] Checking cookies BEFORE API call:`, document.cookie);
    
    // const startTime = performance.now();
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include' // Important for cookies
    });
    // const endTime = performance.now();
    
    // debug && console.log(`[Cookie DEBUG][${timestamp}] API call took ${(endTime - startTime).toFixed(2)}ms`);
    // debug && console.log(`[Cookie DEBUG][${timestamp}] API response status: ${response.status}`);
    const result = await response.json();
    // debug && console.log(`[Cookie DEBUG][${timestamp}] Initialization result:`, result);
    
    // Check cookies after a small delay to allow browser to update
    setTimeout(() => {
      // debug && console.log(`[Cookie DEBUG][${timestamp}] Checking cookies AFTER API call:`, document.cookie);
    }, 200);
    return result;
  } catch (error) {
    console.error(`[Cookie] Error initializing visitor cookie for ${siteName}:`, error);
    return {
      success: false,
      error: `Failed to initialize visitor cookie: ${error.message}`
    };
  }
};

/**
 * Clear the visitor cookie
 * Should be called when consent is revoked or user opts out
 *
 * @param {Object} config - Configuration object
 * @param {string} config.siteName - Site identifier for API calls
 * @param {string} config.backendBaseUrl - Base URL for backend API
 * @param {boolean} [config.debug=false] - Enable debug logging 
 * @returns {Promise<Object>} Promise with the API response
 */
export const clearVisitorCookie = async ({ siteName, backendBaseUrl, debug = false }) => {
  try {
    // Always log this for debugging
    // const timestamp = new Date().toISOString();
    // debug && console.log(`[Cookie DEBUG][${timestamp}] Clearing visitor cookie for ${siteName}`);
    const endpoint = `${backendBaseUrl}/chat-api/${siteName}/clear-cookie`;
    // debug && console.log(`[Cookie DEBUG][${timestamp}] API call to: ${endpoint}`);
    
    // debug && console.log(`[Cookie DEBUG][${timestamp}] Checking cookies BEFORE clearing:`, document.cookie);
    
    // const startTime = performance.now();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include' // Important for cookies
    });
    // const endTime = performance.now();
    
    // debug && console.log(`[Cookie DEBUG][${timestamp}] Clear API call took ${(endTime - startTime).toFixed(2)}ms`);
    // debug && console.log(`[Cookie DEBUG][${timestamp}] API response status: ${response.status}`);
    const result = await response.json();
    // debug && console.log(`[Cookie DEBUG][${timestamp}] Clearing result:`, result);
    
    // Check cookies after a small delay to allow browser to update
    setTimeout(() => {
      // debug && console.log(`[Cookie DEBUG][${timestamp}] Checking cookies AFTER clearing:`, document.cookie);
    }, 200);
    return result;
  } catch (error) {
    console.error(`[Cookie] Error clearing visitor cookie for ${siteName}:`, error);
    return {
      success: false,
      error: `Failed to clear visitor cookie: ${error.message}`
    };
  }
};

/**
 * Check if a chat session exists via backend API
 * This is the proper way to check for HttpOnly cookie existence
 * 
 * NOTE: Ideally, this would call a dedicated /session-status endpoint
 * This implementation uses the conversation endpoint as a proxy to check session existence
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.siteName - Site identifier for API calls
 * @param {string} config.backendBaseUrl - Base URL for backend API
 * @param {boolean} [config.debug=false] - Enable debug logging 
 * @returns {Promise<{exists: boolean, ephemeral: boolean}>} Promise with session existence status
 */
export const checkChatSessionExists = async ({ siteName, backendBaseUrl, debug = false }) => {
  try {
    // debug && console.log(`[Cookie] Checking if chat session exists for ${siteName}`);
    
    // Use the conversation endpoint as a proxy to check for session existence
    // When a cookie exists, this endpoint will return the conversation history
    // When no cookie exists, it will return an empty message array
    const endpoint = `${backendBaseUrl}/chat-api/${siteName}/conversation`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5-second timeout
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Important for cookies
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    const result = await response.json();
    // debug && console.log('[Cookie] Session check via conversation endpoint:', result);
    
    // If this returns a non-ephemeral result with conversation, a valid session exists
    const hasSession = result.success === true && result.ephemeral !== true;
    
    return {
      exists: hasSession,
      ephemeral: result.ephemeral === true,
      // Include any messages for debugging
      messages: result.messages?.length || 0
    };
  } catch (error) {
    console.error(`[Cookie] Error checking chat session for ${siteName}:`, error);
    return {
      exists: false,
      ephemeral: true,
      error: `Failed to check session status: ${error.message}`
    };
  }
};
